// Structured case facts + per-artifact readiness.
//
// Backend distils every uploaded file into a structured "who/how-much/
// when/where" blob (see apps/api/src/domains/case-facts). The SPA reads
// it here to:
//   - Render the completion bar above the artifacts pane.
//   - Disable artifact buttons whose required facts are missing.
//   - Show the user a friendly "missing: contractor NIF, completion
//     deadline" hint when they hover the disabled button.
//
// The endpoint never blocks waiting for an LLM call — facts are
// regenerated as a fire-and-forget side effect of the dedup trigger,
// not on read.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';
import { useMe } from './auth';

export type CaseFacts = {
  homeowner: Party;
  contractor: Party;
  contract: ContractFacts;
  dispute: DisputeFacts;
};

export type Party = {
  name: string | null;
  company: string | null;
  nif: string | null;
  address: string | null;
  email: string | null;
  phone: string | null;
  impicAlvara: string | null;
};

export type ContractFacts = {
  agreedAt: string | null;
  totalAmountEur: number | null;
  advancePaidEur: number | null;
  finalPaidEur: number | null;
  completionDeadline: string | null;
  workScope: string | null;
  propertyAddress: string | null;
};

export type DisputeFacts = {
  primaryGrievance: string | null;
  keyDefects: string[];
  abandonmentDate: string | null;
  keyDates: Array<{ label: string; date: string }>;
};

export type Completeness = {
  ratio: number;
  filledCount: number;
  totalCount: number;
  missing: Array<{ path: string; label: string }>;
};

export type ArtifactReadiness = {
  /** Combined gate: both facts AND evidence must be ready. */
  ready: boolean;
  /** Missing case-facts (e.g. contractor NIF, completion deadline). */
  missing: Array<{ path: string; label: string }>;
  /** Whether the evidence-coverage gate is satisfied. Distinct from
   *  facts so the UI can surface the two reasons separately. */
  evidenceReady: boolean;
  /** Timeline events blocking generation because they lack documentary
   *  proof (e.g. an agreement event sourced only from chat). */
  evidenceGaps: Array<{ eventId: string; eventDate: string; summary: string; reason: string }>;
};

export type CaseFactsResponse = {
  facts: CaseFacts | null;
  completeness: Completeness;
  readiness: Record<string, ArtifactReadiness>;
  /** Paths the user has explicitly set via the Data tab or chat. UI uses
   *  this to render the "You" / "AI" source chip per field. */
  manualPaths: string[];
  /** Allow-list of paths the Data tab is permitted to edit. Matches
   *  MANUAL_EDITABLE_PATHS on the server. */
  editablePaths: string[];
};

export const caseFactsQueryKey = (caseId: string) => ['cases', caseId, 'case-facts'] as const;

export function useCaseFacts(caseId: string | undefined) {
  const me = useMe();
  return useQuery({
    queryKey: caseFactsQueryKey(caseId ?? ''),
    queryFn: () => apiFetch<CaseFactsResponse>(`/cases/${caseId}/case-facts`),
    enabled: !!caseId && !!me.data?.user,
    // Refresh every 10s while completeness is < 1 — facts are populated
    // async after dedup, so the user sees the bar climb during onboarding.
    refetchInterval: (q) => {
      const ratio = q.state.data?.completeness.ratio ?? 0;
      return ratio < 1 ? 10_000 : false;
    },
  });
}

/** Manual refresh — kicks the LLM. Useful when the user added evidence
 *  and wants the fact sheet refreshed without waiting for the next
 *  dedup trigger to fire. */
export function useRefreshCaseFacts(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ facts: CaseFacts | null; completeness: Completeness }>(
        `/cases/${caseId}/case-facts/refresh`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: caseFactsQueryKey(caseId) }),
  });
}

/**
 * Edit a single fact path manually. Optimistic update — the cached facts
 * blob is patched immediately so the completion strip and Data form
 * reflect the new value without waiting for a server round-trip.
 */
export function useUpdateCaseFact(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { path: string; value: string | number | null }) =>
      apiFetch<{ ok: true }>(`/cases/${caseId}/case-facts/manual`, {
        method: 'PATCH',
        body: vars,
      }),
    onMutate: async (vars) => {
      const key = caseFactsQueryKey(caseId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<CaseFactsResponse>(key);
      if (previous) {
        const next = patchFactsLocally(previous, vars.path, vars.value);
        qc.setQueryData<CaseFactsResponse>(key, next);
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(caseFactsQueryKey(caseId), ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: caseFactsQueryKey(caseId) }),
  });
}

/** Clear a manual override — lets AI extraction take over the path again. */
export function useClearCaseFact(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (path: string) =>
      apiFetch<{ ok: true }>(
        `/cases/${caseId}/case-facts/manual?path=${encodeURIComponent(path)}`,
        {
          method: 'DELETE',
        },
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: caseFactsQueryKey(caseId) }),
  });
}

/**
 * Patch a single dotted path inside a CaseFactsResponse without mutating
 * the original. Used for optimistic updates so the UI reflects the user's
 * edit before the server has confirmed.
 */
function patchFactsLocally(
  prev: CaseFactsResponse,
  path: string,
  value: string | number | null,
): CaseFactsResponse {
  // Deep-clone to avoid touching the original cache entry.
  const next: CaseFactsResponse = JSON.parse(JSON.stringify(prev));
  const facts = (next.facts ?? {
    homeowner: emptyParty(),
    contractor: emptyParty(),
    contract: emptyContract(),
    dispute: emptyDispute(),
  }) as Record<string, unknown>;
  setByPath(facts, path, value);
  next.facts = facts as unknown as CaseFacts;
  // Update manualPaths set.
  const set = new Set(next.manualPaths ?? []);
  if (value === null) set.delete(path);
  else set.add(path);
  next.manualPaths = Array.from(set);
  return next;
}

function setByPath(target: Record<string, unknown>, path: string, value: unknown) {
  const parts = path.split('.');
  let cursor: Record<string, unknown> = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i] as string;
    if (typeof cursor[key] !== 'object' || cursor[key] === null) {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }
  cursor[parts[parts.length - 1] as string] = value;
}

function emptyParty(): Party {
  return {
    name: null,
    company: null,
    nif: null,
    address: null,
    email: null,
    phone: null,
    impicAlvara: null,
  };
}
function emptyContract(): ContractFacts {
  return {
    agreedAt: null,
    totalAmountEur: null,
    advancePaidEur: null,
    finalPaidEur: null,
    completionDeadline: null,
    workScope: null,
    propertyAddress: null,
  };
}
function emptyDispute(): DisputeFacts {
  return {
    primaryGrievance: null,
    keyDefects: [],
    abandonmentDate: null,
    keyDates: [],
  };
}
